import { useEffect, useMemo, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { useSelector } from "react-redux";
import { mapValues } from "lodash";
import type {
  ExtraLoginParams,
  ModifiedAnalyticsType,
} from "@shared/analytics/types";
import { useTheme } from "styled-components";
import type { AuthUser } from "@shared/types";
import { useRefValue } from "@shared/utils/hooks/misc";
import { selectWorkspace } from "@shared/slices/common.slice";

interface AnalyticsEventContext {
  user?: AuthUser;
  isDarkMode: boolean;
  isTelegramWebApp?: boolean;
}

export const useAnalyticsProvider = <
  AnalyticsType extends { userLoggedIn: (args: any) => void }
>(
  analytics: AnalyticsType,
  onIdentityChange: (userId: string | undefined, allowed: boolean) => void,
  userSystemDarkModeOn?: boolean,
  isTelegramWebApp?: boolean
) => {
  const theme = useTheme();
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);
  const workspace = useSelector(selectWorkspace);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializedUserId, setInitializedUserId] = useState<string>();
  const [extraLoginParams, setExtraLoginParams] = useState<ExtraLoginParams>();
  const lastLoginId = useRef<string | undefined>(undefined);
  const contextRef = useRefValue<AnalyticsEventContext>({
    user,
    isDarkMode: theme.isDarkMode,
    isTelegramWebApp,
  });
  const modifiedAnalytics = useMemo(
    () => createAnalytics(analytics, contextRef),
    [analytics]
  );
  useEffect(() => {
    if (!isInitialized) return;
    const userId = user?.id.toString();
    onIdentityChange(userId, !!user?.should_track);
    if (!userId) lastLoginId.current = undefined;
    if (!user?.should_track || !userId) return setInitializedUserId(undefined);
    if (lastLoginId.current !== userId) {
      lastLoginId.current = userId;
      analytics.userLoggedIn(
        addEventContext(
          {
            userId_: userId,
            userSystemDarkModeOn,
            source: extraLoginParams?.source,
            sourceButton: extraLoginParams?.source_button,
            isNewUser: extraLoginParams?.is_new_user,
          },
          contextRef.current
        )
      );
      setExtraLoginParams(undefined);
    }
    setInitializedUserId(userId);
  }, [
    isInitialized,
    user?.id,
    user?.should_track,
    workspace,
    onIdentityChange,
    modifiedAnalytics,
  ]);
  return {
    deferredAnalytics: modifiedAnalytics,
    initializedUserId,
    setInitializedUserId,
    isInitialized,
    setIsInitialized,
    setExtraLoginParams,
    modifiedAnalytics:
      isInitialized &&
      initializedUserId === user?.id.toString() &&
      user?.should_track
        ? modifiedAnalytics
        : undefined,
  };
};

const addEventContext = (
  args: Record<string, unknown> | undefined,
  context: AnalyticsEventContext
) => ({
  ...args,
  epUserSubscriptionPlan: context.user?.package,
  epUserIsTelegramConnected: context.user?.is_telegram_connected,
  epUserIsDiscordConnected: context.user?.is_discord_connected,
  epUserFeatureFlags: context.user?.feature_flags,
  epUserSubscriptionIsActive: true,
  epUserIsAdmin: context.user?.is_admin,
  epUserIsMobileAppUser: context.user?.is_mobile_app_user,
  epUserIsOnboarding: context.user?.is_onboarding,
  epUserIsDarkMode: context.isDarkMode,
  isTelegramMiniApp: context.isTelegramWebApp,
});

const createAnalytics = <AnalyticsType extends object>(
  analytics: AnalyticsType,
  contextRef: MutableRefObject<AnalyticsEventContext>
): ModifiedAnalyticsType<AnalyticsType> =>
  mapValues(analytics, (value) =>
    typeof value !== "function"
      ? value
      : (args: Record<string, unknown>) => {
          if (!contextRef.current.user?.should_track) return;
          return value(addEventContext(args, contextRef.current));
        }
  ) as ModifiedAnalyticsType<AnalyticsType>;
